import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import Dropdown from 'antd/es/dropdown';
import Card from 'antd/es/card';

import Button from 'components/Button';
import { Text } from 'components/Text';
import { Space } from 'components/layout';

import useChat from './hooks/useChat';

import Chatbot from './Chat';

import { CHATBOT_NAME } from './constants';

const headerHeight = '114px';

const ChatDropdown = ({ allowClickAway = true, allowEsc = true, onOpen, onClose }) => {
  const chatState = useChat();
  const chatDropdownRef = useRef(null);

  useEffect(() => {
    if (!chatState.historyLoaded) {
      chatState.loadHistory();
    }
  }, [chatState]);

  const handleOpen = useCallback(() => {
    chatState.show();
    if (onOpen) onOpen();
  }, [chatState, onOpen]);

  const handleClose = useCallback(() => {
    chatState.hide();
    if (onClose) onClose();
  }, [chatState, onClose]);

  const handleToggle = useCallback(() => {
    if (chatState.isVisible) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [chatState, handleOpen, handleClose]);


  useEffect(() => {
    const onClickOutside = (event) => {
      if (!allowClickAway || !chatState.isVisible) return;

      const chatWrapperEl = chatDropdownRef?.current
      if (!chatWrapperEl || chatWrapperEl.contains(event.target)) {
        return
      }

      handleClose();
    };

    document.addEventListener("click", onClickOutside, false);

    return () => {
      document.removeEventListener("click", onClickOutside);
    };
  }, [allowClickAway, chatDropdownRef, chatState.isVisible, handleClose]);

  useEffect(() => {
    const onEscKey = (event) => {
      if (!allowEsc || !chatState.isVisible) return;

      if (event.key === "Escape") handleClose();
    };

    document.addEventListener("keydown", onEscKey, false);

    return () => {
      document.removeEventListener("keydown", onEscKey);
    };
  }, [allowEsc, chatState.isVisible, handleClose]);

  useEffect(() => {
    // close chat on unmount
    return () => {
      return handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Dropdown
      overlay={() => (
        <ChatCard
          style={{
            height: `calc(100vh - ${headerHeight} - 16px)`,
            marginTop: '16px',
            marginRight: '16px',
            borderRadius: '16px'
          }}
          title={(
            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
              <Text
                style={{
                  color: '#45494D',
                  fontSize: '16px',
                  lineHeight: '24px',
                  fontWeight: 600,
                }}
              >
                {CHATBOT_NAME}
              </Text>
              <Button
                type="text"
                size="small"
                icon={{
                  name: 'x',
                  size: 20,
                  style: { marginTop: '2px' }
                }}
                onClick={handleClose}
              />
            </Space>
          )}
        >
          <Chatbot />
        </ChatCard>
      )}
      overlayStyle={{
        minWidth: '375px',
        maxWidth: '375px',
        zIndex: 100,
      }}
      placement="bottomCenter"
      trigger="click"
      onVisibleChange={handleToggle}
      visible={chatState.isVisible}
    >
      <Button type="primary" shape="circle" size="large" icon={{ name: "lightSparkle", size: 20, style: { marginTop: '3px' } }} />
    </Dropdown>
  );
};

const ChatCard = styled(Card).withConfig({
  shouldForwardProp: (propName) => !['selectable', 'expandIcon'].includes(propName),
})`
  .ant-dropdown-menu-head-title {
    padding: 16px 24px;
    box-shadow: 0px -1px 0px 0px #EFF2F5 inset;
  }

  .ant-dropdown-menu-body {
    height: calc(100% - 64px); // remove height of header
  }
`;

export default ChatDropdown;